import { Component } from 'react';
import { connect } from 'react-redux';
import { IAppState } from 'redux/store/types';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Label, ResponsiveContainer, Cell } from 'recharts';
import { IUsageMetricsDataItem } from 'redux/analytics/types';
import { prepareReadUsageMetricsCommandAction, setUsageMetricsDataAction } from 'redux/analytics/actions';
import { withTranslation, WithTranslation } from 'react-i18next';

class Analytics extends Component<IAnalyticsProps> {
  componentDidMount() {
    this.props.prepareReadUsageMetricsCommandAction();
  }

  render() {
    const { t } = this.props;

    // Transform usage metrics data for charts
    const usageChartData = this.props.usageMetricsData.map((item) => {
      const dateString = item.daily_bucket || item.six_hour_bucket || new Date().toISOString();
      const date = new Date(dateString);
      const timeLabel = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      return {
        timeLabel,
        usage_count: item.usage_count,
        total_cost: parseFloat(item.total_cost.toString()),
        total_prompt_tokens: item.total_prompt_tokens,
        total_completion_tokens: item.total_completion_tokens,
        total_reasoning_tokens: item.total_reasoning_tokens,
      };
    });

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#ff0000', '#0000ff', '#00ff00', '#ff00ff', '#00ffff', '#ffff00'];
    const isNarrowScreen = window.innerWidth < 900;
    const itemWidthPercentage = isNarrowScreen ? 15 : 10;
    const minScreenWidthPercentage = isNarrowScreen ? 80 : 40;

    // Calculate max values for proper Y-axis scaling
    const maxUsageCount = Math.max(...usageChartData.map((item) => item.usage_count));
    const maxCost = Math.max(...usageChartData.map((item) => item.total_cost));
    // const maxTokens = Math.max(...usageChartData.map((item) => item.total_prompt_tokens + item.total_completion_tokens + item.total_reasoning_tokens));

    return (
      <div className='page'>
        <header className='header'>{t('menu.analytics')}</header>

        {/* Usage Metrics Charts */}
        {usageChartData.length > 0 ? (
          <>
            <h3>{t('tokenUsage.totalCost')}</h3>
            <ResponsiveContainer width={`${Math.min(Math.max(usageChartData.length * itemWidthPercentage, minScreenWidthPercentage), 100)}%`} height={300}>
              <LineChart data={usageChartData} style={{ backgroundColor: '#fafafa' }} margin={{ top: 30, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='timeLabel' angle={-45} textAnchor='end' height={80}>
                  <Label value='Time (daily)' position='insideBottom' offset={-10} />
                </XAxis>
                <YAxis domain={[0, maxCost * 1.5]} tickCount={4} tickFormatter={(value) => `$${Number(value).toFixed(3)}`} />
                <Tooltip formatter={(value) => [`$${Number(value).toFixed(4)}`, 'Cost']} />
                <Legend />
                <Line type='monotone' dataKey='total_cost' stroke={colors[1]} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>

            <h3>{t('tokenUsage.usageCount')}</h3>
            <ResponsiveContainer width={`${Math.min(Math.max(usageChartData.length * itemWidthPercentage, minScreenWidthPercentage), 100)}%`} height={300}>
              <BarChart data={usageChartData} style={{ backgroundColor: '#fafafa' }} margin={{ top: 30, right: 30, left: 20, bottom: 5 }} barSize={60}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='timeLabel' angle={-45} textAnchor='end' height={80}>
                  <Label value='Time (daily)' position='insideBottom' offset={-10} />
                </XAxis>
                <YAxis domain={[0, Math.ceil(maxUsageCount * 1.1)]} allowDecimals={false} tickFormatter={(value) => value.toLocaleString()} />
                <Tooltip formatter={(value) => [value.toLocaleString(), 'Usage Count']} />
                <Legend />
                <Bar dataKey='usage_count' name='Usage Count' fill={colors[0]}>
                  {usageChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* <h3>Token Usage by 6-Hour Buckets</h3>
            <ResponsiveContainer width={`${Math.min(Math.max(usageChartData.length * itemWidthPercentage, minScreenWidthPercentage), 100)}%`} height={300}>
              <BarChart data={usageChartData} style={{ backgroundColor: '#fafafa' }} margin={{ top: 30, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='timeLabel' angle={-45} textAnchor='end' height={80}>
                  <Label value='Time (6-hour buckets)' position='insideBottom' offset={-10} />
                </XAxis>
                <YAxis domain={[0, Math.ceil(maxTokens * 1.1)]} />
                <Tooltip />
                <Legend />
                <Bar dataKey='total_prompt_tokens' name='Prompt Tokens' fill={colors[2]} />
                <Bar dataKey='total_completion_tokens' name='Completion Tokens' fill={colors[3]} />
                <Bar dataKey='total_reasoning_tokens' name='Reasoning Tokens' fill={colors[4]} />
              </BarChart>
            </ResponsiveContainer> */}
          </>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h3>No Usage Data Available</h3>
            <p>No usage metrics data found. This could be because:</p>
            <ul style={{ textAlign: 'left', display: 'inline-block' }}>
              <li>No AI operations have been performed yet</li>
              <li>The usage metrics collection system is not running</li>
              <li>There's no data for your current user account</li>
            </ul>
            <p>Try performing some AI operations (like creating records or subrecords) and then refresh this page.</p>
          </div>
        )}
      </div>
    );
  }
}

interface IAnalyticsProps extends Omit<WithTranslation, 't'> {
  t: (key: string, options?: any) => string;
  usageMetricsData: IUsageMetricsDataItem[];
  prepareReadUsageMetricsCommandAction: () => void;
  setUsageMetricsDataAction: (data: IUsageMetricsDataItem[]) => void;
}

const mapStateToProps = (state: IAppState) => ({
  usageMetricsData: state.analytics.usageMetricsData,
});

const mapDispatchToProps = {
  prepareReadUsageMetricsCommandAction,
  setUsageMetricsDataAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Analytics));
